import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdAdd,
  MdFilterListOff,
  MdRefresh,
  MdStorefront,
} from 'react-icons/md';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { DataPagination } from '@components/ui/DataPagination';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { TabFilter } from '@components/ui/TabFilter';
import { ApmcFormDialog } from '@features/apmcs/components/ApmcFormDialog';
import { ApmcsGrid } from '@features/apmcs/components/ApmcsGrid';
import { useApmcs } from '@features/apmcs/hooks';
import {
  type Apmc,
  type ApmcSortKey,
  type ListApmcsParams,
  type SortDirection,
} from '@features/apmcs/schema';
import { useStateOptions } from '@features/states/hooks';
import type { StateOption } from '@features/states/schema';
import { useDistrictOptions } from '@features/districts/hooks';
import type { DistrictOption } from '@features/districts/schema';

const DEFAULT_PAGE_SIZE = 50;

type ActiveTabValue = '' | 'true' | 'false';

const ACTIVE_TABS: ReadonlyArray<{ value: ActiveTabValue; label: string }> = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export default function ApmcsScreen() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTabValue>('');
  const [stateId, setStateId] = useState<number | null>(null);
  const [districtId, setDistrictId] = useState<number | null>(null);
  const [sort, setSort] = useState<{
    sortBy?: ApmcSortKey;
    sortDir?: SortDirection;
  }>({});

  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchText, activeTab, stateId, districtId, pageSize, sort]);

  const params = useMemo<ListApmcsParams>(
    () => ({
      page,
      pageSize,
      searchText: searchText || undefined,
      stateId: stateId ?? undefined,
      districtId: districtId ?? undefined,
      isActive: activeTab || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
    }),
    [page, pageSize, searchText, stateId, districtId, activeTab, sort],
  );

  const { data, isLoading, isFetching, refetch } = useApmcs(params);
  const { data: states = [] } = useStateOptions();
  const { data: districts = [] } = useDistrictOptions();

  const stateById = useMemo(
    () => new Map(states.map((s) => [s.id, s])),
    [states],
  );
  const stateValue: StateOption | null =
    stateId == null ? null : stateById.get(String(stateId)) ?? null;

  // Districts cascade by selected state.
  const districtOptions = useMemo<DistrictOption[]>(
    () =>
      stateId == null
        ? []
        : districts.filter((d) => d.stateId === String(stateId)),
    [districts, stateId],
  );
  const districtById = useMemo(
    () => new Map(districtOptions.map((d) => [d.id, d])),
    [districtOptions],
  );
  const districtValue: DistrictOption | null =
    districtId == null ? null : districtById.get(String(districtId)) ?? null;
  const districtDisabled = stateId == null;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Apmc | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row: Apmc) => {
    setEditing(row);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const totalRecords = data?.paginationDetails?.totalRecords ?? 0;
  const hasActiveFilters = Boolean(
    searchText || activeTab || stateId !== null || districtId !== null,
  );
  const isInitialLoad = isLoading && !data;
  const showEmpty = !isLoading && (data?.rowData?.length ?? 0) === 0;

  const clearFilters = () => {
    setSearchInput('');
    setSearchText('');
    setActiveTab('');
    setStateId(null);
    setDistrictId(null);
  };

  const districtFilter = (
    <Autocomplete
      size="small"
      options={districtOptions}
      value={districtValue}
      disabled={districtDisabled}
      onChange={(_, opt) => setDistrictId(opt ? Number(opt.id) : null)}
      getOptionLabel={(opt) => opt.name}
      isOptionEqualToValue={(opt, v) => opt.id === v.id}
      noOptionsText="No districts in this state"
      className="w-full md:w-[200px]"
      // Lock-icon startAdornment dropped — see MarketPriceFiltersBar for the
      // reasoning. Disabled state + Tooltip hover hint communicate the cascade.
      renderInput={(p) => <TextField {...p} label="District" />}
    />
  );

  return (
    <Page
      title="APMCs"
      subtitle="Agricultural Produce Market Committees — the mandis we track."
      fullHeight
      headerRightSlot={
        <TabFilter
          options={ACTIVE_TABS}
          current={activeTab}
          onChange={setActiveTab}
          ariaLabel="Filter by active state"
        />
      }
      topBarAction={
        <Button
          variant="contained"
          size="small"
          startIcon={<MdAdd />}
          onClick={openCreate}
        >
          New
        </Button>
      }
    >
      <div className="mb-3 flex flex-col gap-3 rounded-xl bg-surface-muted px-3 py-3 md:flex-row md:flex-wrap md:items-end md:px-4">
        <TextField
          label="Search code or name"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full md:min-w-[220px] md:flex-1"
        />
        <Autocomplete
          size="small"
          options={states}
          value={stateValue}
          onChange={(_, opt) => {
            setStateId(opt ? Number(opt.id) : null);
            setDistrictId(null);
          }}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, v) => opt.id === v.id}
          className="w-full md:w-[200px]"
          renderInput={(p) => <TextField {...p} label="State" />}
        />
        {districtDisabled ? (
          <Tooltip title="Pick a state first to enable District" arrow>
            <span className="block w-full md:inline-block md:w-auto">{districtFilter}</span>
          </Tooltip>
        ) : (
          districtFilter
        )}
        <div className="flex items-end gap-2 md:contents">
          <TextField
            label="Page size"
            size="small"
            select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="flex-1 md:w-[110px] md:flex-initial"
          >
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <div className="flex items-center gap-2 md:ml-auto">
            {hasActiveFilters && (
              <Button variant="text" size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
            <Tooltip title={isFetching ? 'Loading…' : 'Refresh'}>
              <span>
                <IconButton onClick={() => refetch()} size="small" disabled={isFetching}>
                  {isFetching ? <CircularProgress size={16} thickness={5} /> : <MdRefresh />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {isFetching && data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '1.5fr', bar: 'w-3/4' },
              { width: '140px', bar: 'w-16' },
              { width: '1fr', bar: 'w-1/2' },
              { width: '1fr', bar: 'w-1/2' },
              { width: '110px', bar: 'w-14' },
              { width: '200px', bar: 'w-24' },
              { width: '100px', empty: true },
            ]}
          />
        ) : showEmpty ? (
          hasActiveFilters ? (
            <PageEmpty
              icon={MdFilterListOff}
              title="No matches"
              message="Nothing matches the current filters. Try widening your search."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <PageEmpty
              icon={MdStorefront}
              title="No APMCs yet"
              message="Add a mandi to start configuring price series."
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<MdAdd />}
                  onClick={openCreate}
                >
                  New APMC
                </Button>
              }
            />
          )
        ) : (
          <ApmcsGrid data={data} onEdit={openEdit} onSortChange={setSort} />
        )}
      </div>

      <DataPagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
      />

      <ApmcFormDialog open={dialogOpen} apmc={editing} onClose={closeDialog} />
    </Page>
  );
}
